import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import hebergementService from '../../services/hebergementService';
import { AppButton, AppModal } from '../ui';

const MAX_VISIBLE_BADGES = 2;

function flattenOptions(groups = []) {
  return groups.flatMap((group) => (group.options ?? []).map((opt) => ({
    value: String(opt.value ?? opt.id),
    label: opt.label ?? opt.libelle ?? String(opt.id),
  })));
}

const SiteFonctionnalitesInlineCell = ({
  siteInfo,
  fonctionnalitesGroups = [],
  apiWorking,
  onUpdated,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftIds, setDraftIds] = useState([]);

  const allOptions = useMemo(() => flattenOptions(fonctionnalitesGroups), [fonctionnalitesGroups]);

  const currentIds = useMemo(
    () => (siteInfo.fonctionnaliteIds ?? []).map((id) => String(id)),
    [siteInfo.fonctionnaliteIds],
  );

  const currentLabels = siteInfo.fonctionnaliteLabels ?? [];

  const openModal = (event) => {
    event.stopPropagation();
    if (!apiWorking || saving) return;
    setDraftIds(currentIds);
    setOpen(true);
  };

  const toggleDraft = (id) => {
    const key = String(id);
    setDraftIds((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ));
  };

  const handleSave = async () => {
    if (!apiWorking || saving) return;

    const normalized = [...draftIds].sort();
    const current = [...currentIds].sort();
    if (normalized.join('|') === current.join('|')) {
      setOpen(false);
      return;
    }

    setSaving(true);
    try {
      const response = await hebergementService.updateFonctionnalites(
        siteInfo.id,
        draftIds.map((id) => Number(id)),
      );
      if (response.success && response.data) {
        onUpdated?.(siteInfo.id, response.data);
      }
      setOpen(false);
    } catch (err) {
      console.error('Erreur mise à jour fonctionnalités:', err);
    } finally {
      setSaving(false);
    }
  };

  const visibleLabels = currentLabels.slice(0, MAX_VISIBLE_BADGES);
  const hiddenCount = Math.max(0, currentLabels.length - MAX_VISIBLE_BADGES);
  const draftCountLabel = `${draftIds.length} sélectionnée${draftIds.length > 1 ? 's' : ''}`;

  return (
    <>
      {visibleLabels.map((label) => (
        <span key={label} className="sw-feat">{label}</span>
      ))}
      {hiddenCount > 0 ? (
        <span className="sw-more">+{hiddenCount}</span>
      ) : null}
      <button
        type="button"
        className="sw-add sw-add--feat"
        title="Choisir les fonctionnalités"
        onClick={openModal}
        disabled={saving}
      >
        <Icon icon={currentLabels.length ? 'lucide:pencil' : 'lucide:plus'} width={12} />
        {currentLabels.length ? 'Modifier' : 'Fonctionnalités'}
      </button>

      <AppModal
        show={open}
        className="sw-feat-modal"
        onClose={() => setOpen(false)}
        header={(
          <div className="sw-modal__head">
            <div>
              <div className="sw-modal__title">Fonctionnalités</div>
              <div className="sw-modal__sub">{siteInfo.name}</div>
            </div>
            <button type="button" className="sw-modal__x" title="Fermer" onClick={() => setOpen(false)}>
              <Icon icon="lucide:x" width={16} />
            </button>
          </div>
        )}
        footer={(
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <AppButton className="sw-btn sw-btn--text" onClick={() => setDraftIds([])} disabled={saving}>
                Tout effacer
              </AppButton>
              <span className="sw-draft-count">{draftCountLabel}</span>
            </div>
            <div className="sw-modal__actions">
              <AppButton className="sw-btn" onClick={() => setOpen(false)} disabled={saving}>Annuler</AppButton>
              <AppButton className="sw-btn sw-btn--dark" onClick={handleSave} loading={saving} loadingLabel="Enregistrement…">
                Enregistrer
              </AppButton>
            </div>
          </>
        )}
      >
        <div className="sw-chips" role="group" aria-label="Fonctionnalités du site">
          {allOptions.map((opt) => {
            const selected = draftIds.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`sw-pill${selected ? ' is-on' : ''}`}
                onClick={() => toggleDraft(opt.value)}
                aria-pressed={selected}
              >
                {selected ? <Icon icon="lucide:check" width={13} /> : null}
                {opt.label}
              </button>
            );
          })}
        </div>
      </AppModal>
    </>
  );
};

export default SiteFonctionnalitesInlineCell;
